// 基本型別定義，基於 schema 定義和 Firestore 資料模型
// ============== [UPDATED v0.7.1] 對齊後端 API 的型別 ==============

/**
 * 精選等級 (Featured Level)
 * 0 = 非精選
 * 1 = 一般精選
 * 2 = 大圖精選
 */
export type FeaturedLevel = 0 | 1 | 2;

/**
 * 類別 (Category) - 對齊後端 API
 * [UPDATED v0.7.1] 改為字串聯集型別，與後端 API 一致
 */
export type Category = '維修' | '保養' | '美容' | '改裝';

/**
 * 地區 (Region) - 對齊後端 API
 * [UPDATED v0.7.1] 改為字串聯集型別，與後端 API 一致
 */
export type Region =
  | '台北市'
  | '新北市'
  | '桃園市'
  | '台中市'
  | '台南市'
  | '高雄市'
  | '基隆市'
  | '新竹市'
  | '新竹縣'
  | '苗栗縣'
  | '彰化縣'
  | '南投縣'
  | '雲林縣'
  | '嘉義市'
  | '嘉義縣'
  | '屏東縣'
  | '宜蘭縣'
  | '花蓮縣'
  | '台東縣'
  | '澎湖縣'
  | '金門縣'
  | '連江縣';

/**
 * 類別常數
 */
export const CATEGORIES: Category[] = ['維修', '保養', '美容', '改裝'];

/**
 * 地區常數
 */
export const REGIONS: Region[] = [
  '台北市', '新北市', '桃園市', '台中市',
  '台南市', '高雄市', '基隆市', '新竹市',
  '新竹縣', '苗栗縣', '彰化縣', '南投縣',
  '雲林縣', '嘉義市', '嘉義縣', '屏東縣',
  '宜蘭縣', '花蓮縣', '台東縣', '澎湖縣',
  '金門縣', '連江縣'
];

// ============== 舊版型別 (保留以避免破壞現有程式碼) ==============

/**
 * User Role
 */
export type UserRole = 'member' | 'moderator' | 'admin' | 'super_admin';

/**
 * 地區分類
 * 對應 WordPress taxonomy: region
 * [DEPRECATED v0.7.1] 請使用 Region 字串型別
 */
export interface RegionDetail {
  id: string;
  slug: string;
  name: string;
  order: number;
  description_en?: string;
  location_coordinates?: string;
}

/**
 * 服務類型分類
 * 對應 WordPress taxonomy: service
 * [DEPRECATED v0.7.1] 請使用 Category 字串型別
 */
export interface Service {
  id: string;
  slug: string;
  name: string;
  order: number;
  description_en?: string;
  icon?: string;
  parent_slug?: string;
}

/**
 * 廣告統計資料
 */
export interface AdStats {
  impressions: number;
  clicks: number;
}

/**
 * 廣告內容
 * [UPDATED v0.7.1] 對齊後端 API (workers/src/types.ts)
 */
export interface Ad {
  id: string;
  title: string;
  description: string | null;
  category: Category;           // '維修', '保養', '美容', '改裝'
  region: Region;                // '台北市', '新北市', ...
  district: string | null;
  storeName: string;
  phone: string | null;
  address: string | null;
  priceRange: string | null;     // '$', '$$', '$$$', '$$$$'
  imageUrls: string[];
  tags: string[];
  status: 'active' | 'inactive' | 'pending' | 'rejected';
  viewCount: number;
  clickCount: number;
  memberId: string | null;
  featured: FeaturedLevel;       // [FRONTEND] 精選廣告標記 (0=非精選, 1=一般精選, 2=大圖精選)
  createdAt: string;
  updatedAt: string;

  // [FRONTEND] Mobile/UI specific fields (v0.7.1+)
  contactEmail?: string | null;     // 聯絡 Email
  contactWebsite?: string | null;   // 網站 URL
  businessHours?: string | null;    // 營業時間
  videoUrl?: string | null;         // 影片 URL
  specialOffer?: string | null;     // 特別優惠
}

/**
 * 廣告申請
 * 對應 Firestore collection: adRequests
 */
export interface AdRequest {
  id: string;
  requesterUid: string;
  title: string;
  proposedContent: string;
  regions: string[];
  services: string[];
  attachments: string[]; // Storage URLs
  status: 'submitted' | 'review' | 'approved' | 'rejected';
  notes?: string; // admin notes
  createdAt: string;
  updatedAt: string;
}

/**
 * 許願內容
 * [UPDATED v0.7.1] 對齊後端 API (workers/src/types.ts)
 */
export interface Wish {
  id: string;
  title: string;
  description: string | null;
  category: Category;           // '維修', '保養', '美容', '改裝'
  region: Region;                // '台北市', '新北市', ...
  district: string | null;
  budget: string | null;         // "3000-5000", "5000+"
  urgent: boolean;
  status: 'open' | 'matched' | 'closed';
  userName: string | null;
  userPhone: string | null;
  userEmail: string | null;
  matchedMembers: string[];      // JSON array
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 會員資料
 * [UPDATED v0.7.1] 對齊後端 API (workers/src/types.ts)
 */
export interface Member {
  id: string;                   // [UPDATED] from uid
  email: string;
  name: string;                 // [UPDATED] from displayName
  storeName: string | null;     // [UPDATED] from companyName
  phone: string | null;
  role: 'member' | 'moderator' | 'admin' | 'super_admin';  // [UPDATED] values - vip removed, synced with backend
  plan: 'free' | 'basic' | 'premium';  // Membership plan for usage limits
  status: 'active' | 'inactive' | 'suspended' | 'pending_email';  // [UPDATED] added pending_email for LINE users without email
  lastLoginAt?: string;

  // v0.10.1: Email verification status
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;

  // [FRONTEND] Mobile/UI specific fields (v0.7.1+)
  serviceAreas?: string[];      // 服務地區 (region slugs)
  serviceTypes?: string[];      // 服務類型 (service slugs)

  // [LINE LIFF] LINE Account Linking (v0.9.1+)
  lineUserId?: string;          // LINE User ID (when linked)
  lineDisplayName?: string;     // LINE display name
  linePictureUrl?: string;      // LINE profile picture URL

  // v1.0.0: Password status (for password change functionality)
  hasPassword?: boolean;        // Whether user has a password set (false for LINE-only users)
}

/**
 * 通知偏好設定
 * 對應 Firestore collection: notificationPrefs
 */
export interface NotificationPreference {
  uid: string;
  enabled: boolean;
  regions: string[];
  services: string[];
  email: string;
  lastNotifiedAt?: string;
}

/**
 * 分析事件
 * 對應 Firestore collection: events
 */
export interface AnalyticsEvent {
  id: string;
  type: 'ad_impression' | 'ad_click';
  adId: string;
  uid?: string;
  ts: string;
  context: {
    route?: string;
    referrer?: string;
    device?: string;
  };
}

/**
 * 篩選條件
 */
export interface FilterOptions {
  regions?: string[];
  services?: string[];
  sortBy?: 'latest' | 'popular' | 'featured';
  query?: string;
}

/**
 * 分頁資訊
 */
export interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/**
 * API Wish Type (from backend)
 * [UPDATED v0.7.1] 對齊後端 API，與 Wish 型別一致
 * @deprecated 直接使用 Wish 型別即可
 */
export interface ApiWish {
  id: string;
  title: string;
  description: string | null;
  category: Category;
  region: Region;
  district: string | null;
  budget: string | null;
  urgent: boolean;
  status: 'open' | 'matched' | 'closed';
  userName: string | null;
  userPhone: string | null;
  userEmail: string | null;
  matchedMembers: string[];
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Create Wish Request
 */
export interface CreateWishRequest {
  title: string;
  description?: string;
  category: string;
  region: string;
  district?: string;
  budget?: string;
  urgent?: boolean;
  userName: string;
  userPhone?: string;
  userEmail?: string;
}

/**
 * API Ad Type (from backend)
 * [UPDATED v0.7.1] 對齊後端 API，與 Ad 型別一致
 * @deprecated 直接使用 Ad 型別即可
 */
export interface ApiAd {
  id: string;
  title: string;
  description: string | null;
  category: Category;
  region: Region;
  district: string | null;
  storeName: string;
  phone: string | null;
  address: string | null;
  priceRange: string | null;
  imageUrls: string[];
  tags: string[];
  status: 'active' | 'inactive' | 'pending';
  viewCount: number;
  clickCount: number;
  memberId: string | null;
  featured: FeaturedLevel;       // 0=非精選, 1=一般精選, 2=大圖精選
  createdAt: string;
  updatedAt: string;

  // [FRONTEND] Mobile/UI specific fields (v0.7.1+)
  contactEmail?: string | null;
  contactWebsite?: string | null;
  businessHours?: string | null;
  videoUrl?: string | null;
  specialOffer?: string | null;
}

/**
 * Login Request
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login Response
 */
export interface LoginResponse {
  member: Member;
  token: string;
}

/**
 * Register Request
 */
export interface RegisterRequest {
  displayName: string;
  email: string;
  password: string;
  phone?: string;
  companyName?: string;
  serviceAreas?: string[];
  serviceTypes?: string[];
}

/**
 * Member Usage Limits
 */
export interface MemberUsageLimits {
  maxAds: number | null;
  maxContacts: number | null;
}

/**
 * Member Usage Stats Response
 */
export interface MemberUsageResponse {
  success: true;
  data: {
    currentAds: number;
    currentContacts: number;
    limits: MemberUsageLimits;
  };
}

// ============== v0.6.0 Analytics Types ==============

/**
 * 廣告分析資料
 */
export interface AdAnalytics {
  adId: string;
  title: string;
  views: number;
  clicks: number;
  ctr: number; // 點擊率 (百分比)
  date: string;
}

/**
 * 聯繫分析資料
 */
export interface ContactAnalytics {
  wishId: string;
  title: string;
  contacts: number;
  date: string;
}

/**
 * 會員完整分析資料
 */
export interface MemberAnalytics {
  ads: {
    total: number;
    byStatus: Record<string, number>;
    topViewed: AdAnalytics[];
    topClicked: AdAnalytics[];
  };
  contacts: {
    total: number;
    byMonth: Record<string, number>;
    recent: ContactAnalytics[];
  };
  period: {
    start: string;
    end: string;
  };
}

/**
 * 分析資料回應
 */
export interface AnalyticsResponse {
  success: true;
  data: MemberAnalytics;
}

/**
 * 分析查詢參數
 */
export interface AnalyticsQuery {
  startDate?: string;
  endDate?: string;
}

// ============== v0.6.0 Approval/Workflow Types ==============

/**
 * 審核狀態
 */
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

/**
 * 待審廣告項目
 */
export interface ApprovalQueueItem {
  id: string;
  type: 'ad' | 'wish';
  title: string;
  status: ApprovalStatus;
  createdAt: string;
  memberId?: string;
  memberEmail?: string;
}

/**
 * 待審廣告回應
 */
export interface ApprovalQueueResponse {
  success: true;
  data: ApprovalQueueItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * 審核操作回應
 */
export interface ApprovalActionResponse {
  success: true;
  data: {
    id: string;
    status: ApprovalStatus;
    reviewedAt: string;
    reviewedBy: string;
  };
}

/**
 * 審計日誌項目
 */
export interface AuditLogItem {
  id: string;
  adminId: string;
  adminEmail: string;
  action: 'approve' | 'reject' | 'update' | 'delete';
  targetType: 'ad' | 'wish' | 'member';
  targetId: string;
  targetTitle?: string;
  details: Record<string, unknown>;
  createdAt: string;
}

/**
 * 審計日誌回應
 */
export interface AuditLogsResponse {
  success: true;
  data: AuditLogItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * 會員列表項目
 */
export interface MemberListItem {
  id: string;
  email: string;
  name: string | null;
  role: 'member' | 'moderator' | 'admin' | 'super_admin';
  plan: 'free' | 'basic' | 'premium';
  status: 'active' | 'inactive' | 'suspended';

  // v0.10.1: Email verification status
  emailVerified: boolean;
  createdAt: string;
  adsCount?: number;
  wishesCount?: number;
}

/**
 * 會員列表回應
 */
export interface MemberListResponse {
  success: true;
  data: MemberListItem[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

// ============== v0.7.0 Dashboard Types ==============

/**
 * Dashboard 統計數據 (完全對齊後端 workers/src/lib/dashboard.ts)
 */
export interface DashboardStats {
  members: {
    total: number;
    active: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  ads: {
    total: number;
    active: number;
    pending: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  wishes: {
    total: number;
    active: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  contacts: {
    total: number;
  };
  // Pending approvals - only ads require approval, wishes do not
  pendingApprovals: {
    ads: number;
    wishes: 0; // Always 0 - wishes don't require approval
    total: number;
  };
  charts: {
    membersByPlan: Record<string, number>;
    adsByCategory: Record<string, number>;
    wishesByStatus: Record<string, number>;
  };
  recent: {
    members: Array<{
      id: string;
      name: string;
      email: string;
      plan: string;
      createdAt: string;
    }>;
    ads: Array<{
      id: string;
      title: string;
      category: string;
      region: string;
      status: string;
      createdAt: string;
    }>;
    wishes: Array<{
      id: string;
      title: string;
      category: string;
      region: string;
      status: string;
      createdAt: string;
    }>;
  };
}

/**
 * Dashboard 回應
 */
export interface DashboardResponse {
  success: true;
  data: DashboardStats;
}

/**
 * 趨勢數據點
 */
export interface TrendDataPoint {
  date: string;
  count: number;
}

/**
 * 趨勢數據
 */
export interface TrendData {
  memberGrowth: TrendDataPoint[];
  contentTrend: {
    ads: TrendDataPoint[];
    wishes: TrendDataPoint[];
  };
}

/**
 * Dashboard 趨勢回應
 */
export interface DashboardTrendsResponse {
  success: true;
  data: TrendData;
}

/**
 * 進階搜尋參數
 */
export interface AdvancedSearchParams {
  category?: string;
  categories?: string[]; // 多選類別
  region?: string;
  regions?: string[]; // 多選地區
  district?: string;
  priceRange?: {
    min: number | null;
    max: number | null;
  };
  keyword?: string;
  hasImage?: boolean; // 只顯示有圖片的廣告
  sortBy?: 'price' | 'date' | 'popularity' | 'relevance';
  sortOrder?: 'asc' | 'desc';
  page?: number; // 當前頁碼
  limit?: number; // 每頁數量
}

/**
 * 搜尋 Facets 資料
 */
export interface SearchFacetsData {
  categories: Record<string, number>;
  regions: Record<string, number>;
  priceRanges: Record<string, number>;
}

/**
 * 搜尋歷史項目
 */
export interface SearchHistoryItem {
  id: string;
  name: string;
  params: AdvancedSearchParams;
  createdAt: string;
}

// ============== v0.9.0 Marquee Types ==============

/**
 * 從 API 回傳的公開資料型別
 * 對齊後端 API (workers/src/types.ts: MarqueePublic)
 */
export interface MarqueePublic {
  id: string;
  content: string;
  textColor: string; // Mapped from text_color
  bgColor: string; // Mapped from bg_color
  bgOpacity: number; // Mapped from bg_opacity
  speed: number;
  displayOrder: number; // Mapped from display_order
}

/**
 * 管理後台使用的完整資料型別
 * 對齊後端 API (workers/src/types.ts: MarqueeAdmin)
 */
export interface MarqueeAdmin extends MarqueePublic {
  isActive: boolean; // Mapped from is_active (0/1 -> boolean)
  createdBy: string; // Mapped from created_by
  updatedBy: string | null; // Mapped from updated_by
  createdAt: string; // Mapped from created_at
  updatedAt: string; // Mapped from updated_at
}

/**
 * 新增跑馬燈的 DTO (Data Transfer Object)
 * 對齊後端 API (workers/src/types.ts: CreateMarqueeRequest)
 */
export interface CreateMarqueeDTO {
  content: string;
  textColor?: string; // Optional: uses default if not provided
  bgColor?: string; // Optional: uses default if not provided
  bgOpacity?: number; // Optional: uses default if not provided
  speed?: number; // Optional: uses default if not provided
  isActive?: boolean; // Optional: defaults to true
}

/**
 * 更新跑馬燈的 DTO
 * 對齊後端 API (workers/src/types.ts: UpdateMarqueeRequest)
 */
export interface UpdateMarqueeDTO extends Partial<CreateMarqueeDTO> {
  displayOrder?: number;
}

/**
 * 全域設定型別
 * 對齊後端 API (workers/src/types.ts: MarqueeSettings)
 */
export interface MarqueeSettings {
  enabled: boolean; // Global enable/disable for all marquees
  defaultSpeed: number; // Default scroll speed (seconds/round)
  defaultTextColor: string; // Default text color (hex)
  defaultBgColor: string; // Default background color (hex)
  defaultBgOpacity: number; // Default background opacity (0.0-1.0)
}

/**
 * 拖曳排序項目
 */
export interface MarqueeOrderItem {
  id: string;
  displayOrder: number;
}

/**
 * Marquee 列表回應型別 (公開 API)
 * 對齊後端 API (workers/src/types.ts: MarqueeListResponse)
 */
export interface MarqueeListResponse {
  success: true;
  data: MarqueePublic[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * Marquee 列表回應型別 (管理後台 API)
 * 對齊後端 API (workers/src/types.ts: MarqueeAdminListResponse)
 */
export interface MarqueeAdminListResponse {
  success: true;
  data: MarqueeAdmin[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * 單一 Marquee 回應型別 (公開 API)
 * 對齊後端 API (workers/src/types.ts: MarqueeResponse)
 */
export interface MarqueeResponse {
  success: true;
  data: MarqueePublic;
}

/**
 * 單一 Marquee 回應型別 (管理後台 API)
 * 對齊後端 API (workers/src/types.ts: MarqueeAdminResponse)
 */
export interface MarqueeAdminResponse {
  success: true;
  data: MarqueeAdmin;
}

/**
 * Marquee 設定回應型別
 * 對齊後端 API (workers/src/types.ts: MarqueeSettingsResponse)
 */
export interface MarqueeSettingsResponse {
  success: true;
  data: MarqueeSettings;
}

/**
 * Marquee 查詢參數
 * 對齊後端 API (workers/src/types.ts: GetMarqueesQuery)
 */
export interface GetMarqueesQuery {
  isActive?: boolean; // Filter by active status (admin only)
  limit?: number; // Pagination limit
  offset?: number; // Pagination offset
}

